import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import api, { fmtINR } from "@/lib/api";
import { PageHeader } from "@/components/common";

const RANGES = [["1", "Today"], ["7", "7 days"], ["30", "30 days"], ["90", "3 months"], ["180", "6 months"], ["365", "1 year"]];
const COLORS = ["#FF6B00", "#1D5BD8", "#F5A623", "#138808", "#7C3AED"];

export default function Analytics() {
  const [days, setDays] = useState("30");
  const { data } = useQuery({
    queryKey: ["agent-analytics", days],
    queryFn: async () => (await api.get(`/agent/analytics?days=${days}`)).data,
  });

  const tooltipStyle = { background: "#fff", border: "1px solid #E2EAF3", borderRadius: 8, fontSize: 12, color: "#0F2440" };
  const totals = (data?.channels || []).reduce((a, c) => ({ sales: a.sales + c.sales, profit: a.profit + c.profit, orders: a.orders + c.orders }), { sales: 0, profit: 0, orders: 0 });

  return (
    <div data-testid="agent-analytics-page">
      <PageHeader title="Analytics" sub="Sales, profit and product performance across all your channels." testid="analytics-title">
        <div className="flex rounded-lg border border-slate-200 bg-white p-1">
          {RANGES.map(([v, l]) => (
            <button key={v} data-testid={`range-${v}`} onClick={() => setDays(v)}
              className={`rounded-md px-3 py-1.5 text-[11px] font-semibold transition-colors ${days === v ? "bg-[#FF6B00] text-white" : "text-slate-500"}`}>
              {l}
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="mb-4 grid grid-cols-3 gap-3">
        {[["Sales", fmtINR(totals.sales), "#FF6B00"], ["Orders", totals.orders, "#1D5BD8"], ["Est. Profit", fmtINR(totals.profit), "#138808"]].map(([l, v, c]) => (
          <div key={l} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{l}</p>
            <p className="mt-1 text-xl font-extrabold" style={{ color: c }}>{v}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="analytics-trend">
          <h3 className="mb-4 text-sm font-bold text-[#0F2440]">Sales & Profit Trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data?.series || []}>
              <CartesianGrid stroke="#EDF2F8" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: "#7A90A8", fontSize: 9 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fill: "#7A90A8", fontSize: 9 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="sales" stroke="#FF6B00" fill="#FF6B0022" strokeWidth={2} name="Sales ₹" />
              <Area type="monotone" dataKey="profit" stroke="#138808" fill="#13880822" strokeWidth={2} name="Profit ₹" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="analytics-channels">
          <h3 className="mb-4 text-sm font-bold text-[#0F2440]">Channel Comparison</h3>
          {data?.channels?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.channels} dataKey="sales" nameKey="channel" innerRadius={50} outerRadius={75} paddingAngle={3}>
                  {data.channels.map((c, i) => <Cell key={c.channel} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="py-16 text-center text-xs text-slate-400">No sales in this period yet</p>}
        </div>

        <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm lg:col-span-2" data-testid="analytics-products">
          <h3 className="mb-3 text-sm font-bold text-[#0F2440]">Product Performance</h3>
          {!data?.products?.length ? <p className="py-10 text-center text-xs text-slate-400">Product analytics appear after your first sales.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
                    <th className="px-3 py-2">Product</th>
                    <th className="px-3 py-2">Units</th>
                    <th className="px-3 py-2">Revenue</th>
                    <th className="px-3 py-2">Cost</th>
                    <th className="px-3 py-2">Est. Profit</th>
                    <th className="px-3 py-2">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {data.products.map((p) => (
                    <tr key={p.sku} data-testid={`analytics-product-${p.sku}`} className="border-b border-slate-50">
                      <td className="px-3 py-2 font-medium text-[#0F2440]">{p.name}<span className="ml-1.5 font-mono text-[9px] text-slate-400">{p.sku}</span></td>
                      <td className="px-3 py-2 text-slate-600">{p.qty}</td>
                      <td className="px-3 py-2 text-slate-600">{fmtINR(p.revenue)}</td>
                      <td className="px-3 py-2 text-slate-600">{fmtINR(p.cost)}</td>
                      <td className="px-3 py-2 font-semibold text-[#138808]">{fmtINR(p.profit)}</td>
                      <td className="px-3 py-2 text-slate-600">{p.revenue ? Math.round(p.profit / p.revenue * 100) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
